import { yupResolver } from '@hookform/resolvers/yup';
import SearchBar from '-/Custom/SearchBar';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { TextInput } from 'react-native';
import { useClient, useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import FormCheckboxItem from '@/components/Custom/FormCheckboxItem';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import * as Placeholder from '@/components/Custom/Placeholder';
import * as PersonListItem from '@/components/Person/PersonListItem';
import Box from '@/components/UI/Box';
import List from '@/components/Unistyles/List';
import { graphql } from '@/gql/graphql';
import type { PersonBoolExp } from '@/gql/scalars';
import useSearchablePaginatedQuery from '@/hooks/useSearchablePaginatedQuery';
import useUserId from '@/hooks/useUserId';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';

type ChatDetailInfoPeopleFormValues = {
  searchTerm: string;
  selectedPeople: SelectedPerson[];
};

type SelectedPerson = {
  id: string;
  name: string | null;
  avatarId: string | null;
  color: string | null;
};

const GetChatParticipants = graphql(/* GraphQL */ `
  query GetChatParticipants($chatId: uuid!) {
    chat_by_pk(id: $chatId) {
      id
      chat_type
      chat_people(order_by: { person: { name: asc } }) {
        person_id
        person {
          id
          ...PersonListItemAvatar
          ...PersonListItemName
        }
      }
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const AddChatPeople = graphql(/* GraphQL */ `
  mutation AddChatPeople($people: [chat_person_insert_input!]!) {
    insert_chat_person(objects: $people) {
      returning {
        chat {
          id
          participants {
            ...PersonListItemAvatar
            ...PersonListItemName
          }
        }
      }
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const GetAddMembersPeople = graphql(/* GraphQL */ `
  query GetAddMembersPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      username
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const SearchAddMembersPeople = graphql(/* GraphQL */ `
  query SearchAddMembersPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      username
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const Schema = Yup.object({
  searchTerm: Yup.string().default('').defined(),
  selectedPeople: Yup.array().default([]).defined().min(1).required(),
}).defined();

export default function ChatDetailInfoPeople() {
  const userId = useUserId();
  const { back } = useRouter();
  const { chatId, title } = useLocalSearchParams<{
    chatId?: string;
    title?: string;
  }>();
  const client = useClient();

  const form = useForm<ChatDetailInfoPeopleFormValues>({
    resolver: yupResolver(Schema),
    mode: 'onChange',
    defaultValues: async () => {
      if (!chatId) {
        return { searchTerm: '', selectedPeople: [] };
      }

      const { data } = await client.query(
        GetChatParticipants,
        { chatId },
        { requestPolicy: 'network-only' },
      );

      if (data?.chat_by_pk?.chat_type !== 'group') {
        return { searchTerm: '', selectedPeople: [] };
      }

      return { searchTerm: '', selectedPeople: [] };
    },
  });

  const searchTerm = useWatch({
    control: form.control,
    name: 'searchTerm',
    defaultValue: '',
  });

  const searchInputRef = useRef<TextInput>(null);

  const [{ data }] = useQuery({
    query: GetChatParticipants,
    variables: { chatId: chatId || '' },
    pause: !chatId,
    requestPolicy: 'cache-and-network',
  });

  const chat = data?.chat_by_pk;
  const chatPeople = chat?.chat_people || [];
  const existingIds = chatPeople.flatMap((person) =>
    person.person_id ? [person.person_id] : [],
  );

  const [, addChatPeople] = useMutation(AddChatPeople);

  const filteredIds = [userId, ...existingIds];

  const baseWhere: PersonBoolExp = {
    _and: [
      { contact_access: { viewer_person_id: { _eq: userId } } },
      { is_support: { _eq: false } },
      filteredIds.length ? { id: { _nin: filteredIds } } : {},
    ],
  };

  const searchWhere: PersonBoolExp = {
    _and: [
      baseWhere,
      {
        name: {
          _ilike: `%${searchTerm}%`,
        },
      },
    ],
  };

  const { items: selectablePeople, fetching: fetchingPeople } =
    useSearchablePaginatedQuery({
      searchTerm,
      searchOnMinimum: DEFAULT_SEARCH_MINIMUM,
      pageSize: 25,
      getQuery: GetAddMembersPeople,
      getVariables: ({ limit, offset }) => ({
        where: baseWhere,
        limit,
        offset,
      }),
      selectGetItems: (d) => d?.person ?? [],
      searchQuery: SearchAddMembersPeople,
      searchVariables: ({ searchTerm: term }) => ({
        where: term.length >= DEFAULT_SEARCH_MINIMUM ? searchWhere : baseWhere,
        limit: 25,
        offset: 0,
      }),
      selectSearchItems: (d) => d?.person ?? [],
    });

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: `Add Members${title ? ` - ${title}` : ''}`,
          headerShown: true,
        }}
      />

      <Box p="lg">
        <SearchBar
          ref={searchInputRef}
          onChangeText={(next) =>
            form.setValue('searchTerm', next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          value={searchTerm}
        />
      </Box>

      <List
        renderScrollComponent={(props) => (
          <ModalKeyboardAwareScrollView {...props} />
        )}
        data={selectablePeople}
        fetching={fetchingPeople}
        keyExtractor={(item) => item.id}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon name="people-outline" />
            <Placeholder.Text>No people found</Placeholder.Text>
          </Placeholder.Container>
        }
        renderItem={({ item }) => {
          return (
            <FormCheckboxItem
              name="selectedPeople"
              item={item}
              getItemId={(person) => person.id}
            >
              <PersonListItem.Container>
                <PersonListItem.Avatar fragment={item} />
                <PersonListItem.Content>
                  <PersonListItem.Name fragment={item} />
                </PersonListItem.Content>
              </PersonListItem.Container>
            </FormCheckboxItem>
          );
        }}
      />

      <ModalKeyboardStickyView>
        <FormSaveButton
          info={{ text: 'Save' }}
          onPress={() =>
            form.handleSubmit(async (values) => {
              if (!chatId) {
                return;
              }

              const existingIdSet = new Set(existingIds);
              const insertedPeople = values.selectedPeople.flatMap((person) =>
                existingIdSet.has(person.id)
                  ? []
                  : [
                      {
                        person_id: person.id,
                        chat_id: chatId,
                      },
                    ],
              );

              if (!insertedPeople.length) {
                back();
                return;
              }

              const { error } = await addChatPeople({
                people: insertedPeople,
              });

              if (!error) {
                back();
              }
            })()
          }
        />
      </ModalKeyboardStickyView>
    </FormProvider>
  );
}
